import AddIcon from '@mui/icons-material/Add';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteIconButton from '../components/DeleteIconButton';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, Divider, Grid, IconButton, Link, Typography } from '@mui/material';
import { green } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router';

import { DataAPIContext } from '../api';
import { DELETE } from '../api/dataProvider';
import useBatchDelete from '../bg_tasks/useBatchDelete';
import FixedLinearProgress from '../components/FixedLinearProgress';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import NotificationStringToolbar from './NotificationStringToolbar';
import { TEMPLATE_LANGUAGES } from './utils';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { RulesetPreviewPopover } from '../components/RulesetPreview';
import useConfirmDialog from '../components/useConfirmDialog';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: '#FCFCFE',
        paddingBottom: '1rem !important',
        [theme.breakpoints.down('sm')]: {
            margin: 0,
        },
    },
    rootMobile: {
        [theme.breakpoints.down('sm')]: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            maxHeight: '100%',
            minHeight: 0,
            overflow: 'hidden',
            paddingBottom: `${theme.spacing(1)} !important`,
        },
    },
    mobilePageHeader: {
        [theme.breakpoints.down('sm')]: {
            flexShrink: 0,
            backgroundColor: '#FCFCFE',
            zIndex: 2,
        },
    },
    mobileListRegion: {
        [theme.breakpoints.down('sm')]: {
            flex: 1,
            minHeight: 0,
            maxHeight: 'calc(100dvh - 11rem)',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            paddingBottom: theme.spacing(1),
        },
    },
    stringsCard: {
        [theme.breakpoints.down('sm')]: {
            margin: `${theme.spacing(1)} !important`,
            border: 'none !important',
        },
    },
    recordListWrapper: {
        [theme.breakpoints.down('sm')]: {
            minHeight: 0,
        },
    },
    recordList: {
        border: 'none !important',
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
        height: `calc(${theme.cp.contentHeight} - 11.5rem) !important`,
    },
    dm_enabled: {
        color: green[800],
        fontWeight: 'bold',
    },
    dm_disabled: {
        textDecoration: 'line-through',
    },
}));

const NotificationStringList = (props) => {
    const [confirmDelete, confirmDeleteDialog] = useConfirmDialog();
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'notification_string';
    const classes = useStyles();
    const [active, setActive] = React.useState(null);
    const [selectedIds, setSelectedIds] = React.useState([]);
    const [lastUpdate, setLastUpdate] = React.useState();
    const [syncActive, setSyncActive] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm');
    const { enqueueSnackbar } = useSnackbar();
    const { roleMap, hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    const hasWritePerm = hasPerm('notification', 'write')
    const hasDeletePerm = hasPerm('notification', 'delete')
    const [query, setQuery] = React.useState(null);
    const [loadedRulesets, setLoadedRulesets] = React.useState({});

    const onDeleteString = useCallback(
        async (string_id, ev) => {
            if (await confirmDelete({ body: 'Are you sure you want to delete this string?' })) {
                setSyncActive(true);
                dataAPI(DELETE, resource, { id: string_id })
                    .then(() => {
                        enqueueSnackbar('String deleted successfully', { variant: 'success' });
                        setLastUpdate(new Date());
                    })
                    .catch(() => {
                        enqueueSnackbar('Failed to delete string', { variant: 'error' });
                    })
                    .finally(() => setSyncActive(false));
            }
        },
        [dataAPI, enqueueSnackbar, resource, confirmDelete]
    );

    const onSelectionModelChange = useCallback((ids, selection) => {
        const next = Array.isArray(ids) ? ids : Array.isArray(selection) ? selection : selection?.selectionModel;
        setSelectedIds(Array.isArray(next) ? next : []);
    }, []);

    // CP-7771: bulk delete runs as a background task; the grid refreshes once it has finished.
    const refreshList = useCallback(() => setLastUpdate(new Date()), []);
    const { deleteRecords } = useBatchDelete({ resource, names: ['string', 'strings'], onDeleted: refreshList });
    const onBatchDelete = async () => {
        if (await confirmDelete({ body: `Are you sure you want to delete the ${selectedIds?.length || 0} selected strings?` })) {
            setSyncActive(true);
            deleteRecords(selectedIds)
                .then((accepted) => {
                    if (accepted) setSelectedIds([]);
                })
                .finally(() => setSyncActive(false));
        }
    };

    const recordListColumns = [
        ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
        {
            field: 'sequence',
            headerName: 'Sequence',
            sortable: true,
            filterable: false,
            flex: 0.3,
            type: 'number',
        },
        {
            field: 'name',
            headerName: 'Name',
            sortable: true,
            flex: 1,
            type: 'string',
        },
        {
            field: 'type',
            headerName: 'Type',
            sortable: true,
            flex: 0.5,
            type: 'string',
            valueGetter: (p) => p.row.type?.name,
            // valueOptions: Object.keys(NOTIFICATION_STRING_TYPES).map((k) => ({ value: k, label: NOTIFICATION_STRING_TYPES[k] })),
            // valueFormatter: (p) => NOTIFICATION_STRING_TYPES[p.value],
        },
        {
            field: 'codename',
            headerName: 'Code Name',
            sortable: true,
            flex: 0.5,
            type: 'string',
        },
        {
            field: 'enabled',
            headerName: 'Enabled',
            flex: 0.5,
            type: 'boolean',
        },
        {
            field: 'is_template',
            headerName: 'Is a template',
            flex: 0.5,
            type: 'boolean',
        },
        {
            field: 'template_language',
            headerName: 'Template language',
            sortable: true,
            filterable: true,
            flex: 0.5,
            type: 'singleSelect',
            valueOptions: Object.keys(TEMPLATE_LANGUAGES).map((k) => ({ value: k, label: TEMPLATE_LANGUAGES[k] })),
            valueGetter: (p) => (p.row.template_language && TEMPLATE_LANGUAGES[p.row.template_language]) ? TEMPLATE_LANGUAGES[p.row.template_language] : (p.row.template_language || ''),
        },
        {
            field: 'ruleset',
            headerName: 'Ruleset',
            sortable: true,
            flex: 0.5,
            renderCell: (p) => (p.row.ruleset ? <>
                <RulesetPreviewPopover
                    id={p.row.ruleset.id}
                    ruleset={loadedRulesets[p.row.ruleset.id]}
                    label={<Link href={`/notifications/rulesets/${p.row.ruleset.id}`} target="_blank">{p.row.ruleset.name}</Link>}
                    onChange={(ruleset) => { setLoadedRulesets((prev) => ({...prev, [ruleset.id]: ruleset })) } }
                />
            </> : null),
        },
        {
            field: 'updated_ts',
            headerName: 'Updated at',
            sortable: true,
            flex: 1,
            type: 'dateTime',
            valueGetter: (p) => moment(p.row.updated_ts),
            valueFormatter: (p) => p.value.format('YYYY-MM-DD HH:mm Z')
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            getActions: (p) => [
                <GridActionsCellItem label={hasWritePerm ? 'Edit' : 'View'} icon={hasWritePerm ? <EditIcon/> : <VisibilityIcon/>} color="primary" href={`/notifications/strings/${p.row.id}`} />,
                <DeleteIconButton allowed={hasDeletePerm} onClick={(ev) => onDeleteString(p.row.id, ev)} />,
            ]
        }
    ];

    const listItemRenderer = useCallback(
        (row, idx, rows) => {
            const templateLabel =
                row.template_language && TEMPLATE_LANGUAGES[row.template_language]
                    ? TEMPLATE_LANGUAGES[row.template_language]
                    : row.template_language || '—';
            const updated = row.updated_ts ? moment(row.updated_ts).format('YYYY-MM-DD HH:mm Z') : '—';
            const openRow = () => history.push(`/notifications/strings/${row.id}`);

            return (
                <React.Fragment key={row.id}>
                    <Grid container spacing={1} p={2} alignItems="center">
                        <Grid item xs={12}>
                            <Typography
                                style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}
                                fontWeight={600}
                                onClick={openRow}
                                sx={{ cursor: 'pointer' }}
                            >
                                {row.name}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sx={{ cursor: 'pointer' }} onClick={openRow}>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Sequence: {row.sequence != null && row.sequence !== '' ? row.sequence : '—'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Code Name: {row.codename || '—'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Type: {row.type?.name ?? '—'} · {row.enabled ? 'Enabled' : 'Disabled'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Template: {row.is_template ? 'Yes' : 'No'} · {templateLabel}
                            </Typography>
                            {row.ruleset ? (
                                <Typography variant="body2" color="text.secondary" display="block">
                                    Ruleset:{' '}
                                    <Link
                                        href={`/notifications/rulesets/${row.ruleset.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(ev) => ev.stopPropagation()}
                                    >
                                        {row.ruleset.name}
                                    </Link>
                                </Typography>
                            ) : null}
                            <Typography variant="body2" color="text.secondary" display="block">
                                Updated: {updated}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} container justifyContent="flex-start" spacing={0.5} alignItems="center">
                            {hasPerm('notification', 'read') && (
                                <Grid item>
                                    <IconButton onClick={(ev) => { ev.stopPropagation(); openRow(); }} aria-label="Open string" size="small">
                                        <PersonIcon fontSize="small" color="primary" />
                                    </IconButton>
                                </Grid>
                            )}
                            {hasWritePerm && (
                                <Grid item>
                                    <IconButton onClick={(ev) => { ev.stopPropagation(); openRow(); }} aria-label="Edit string" size="small">
                                        <BorderColorOutlinedIcon fontSize="small" color="primary" />
                                    </IconButton>
                                </Grid>
                            )}
                            <Grid item>
                                <DeleteIconButton
                                    title="Delete string"
                                    allowed={hasDeletePerm}
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        onDeleteString(row.id, ev);
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    {idx < rows.length - 1 ? <Divider /> : null}
                </React.Fragment>
            );
        },
        [hasPerm, hasWritePerm, hasDeletePerm, history, onDeleteString]
    );

    const stringsRecordList = (
        <RecordList
            className={classes.recordList}
            resource={resource}
            selectionModel={selectedIds}
            disableSelectionOnClick={true}
            onSelectionModelChange={onSelectionModelChange}
            checkboxSelection={hasDeletePerm}
            query={query}
            sortModel={[{ field: 'name', sort: 'asc' }]}
            deps={lastUpdate}
            listItemRenderer={isSmallScreen ? listItemRenderer : undefined}
            mobileNaturalHeight={isSmallScreen}
            disableListBorder={!isSmallScreen}
            disablePageUrlData={true}
            columns={recordListColumns}
            noMatchesLabel="No strings"
            noItemsFoundMessage="No strings"
        />
    );

    return (
        <div className={clsx(classes.root, isSmallScreen && classes.rootMobile)}>
            {confirmDeleteDialog}
            <FixedLinearProgress visibility={syncActive} />
            <PageMeta title="Strings" />
            {/* {confirmationDialogAction ? (
                <ConfirmationDialog
                    classes={{ paper: classes.paper }}
                    keepMounted
                    onCancel={confirmationDialogCancel}
                    onConfirm={confirmationDialogConfirm}
                    title={ConfirmationDialogText[confirmationDialogAction].title}
                    okButtonText={ConfirmationDialogText[confirmationDialogAction].okButtonText}
                    cancelButtonText={ConfirmationDialogText[confirmationDialogAction].cancelButtonText}
                    open={!!confirmationDialogAction}
                >
                    {confirmationDialogAction !== 'delete_notification' && confirmationDialogAction !== 'duplicate_notification' && ConfirmationDialogText[confirmationDialogAction].body}
                    {confirmationDialogAction === 'delete_notification' && (
                        <Typography>
                            {ConfirmationDialogText[confirmationDialogAction].body}
                            <span style={{ fontWeight: 700 }}>{active?.name || 'Notification'}</span> This action is irreversible. Are you sure?
                        </Typography>
                    )}
                    {confirmationDialogAction === 'duplicate_notification' && ConfirmationDialogText[confirmationDialogAction].body + (active?.name || 'Notification') + '?'}
                    {confirmationDialogAction === 'duplicate_notification' && (
                        <>
                            <Grid item xs={12} mt={2}>
                                <Typography>Choose the type of notification you want to create:</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={[
                                        { name: 'non_override', id: 0, label: 'Default', value: 0 },
                                        { name: 'override', id: 2, label: 'Tour Specific', value: 2 },
                                    ]}
                                    getOptionLabel={(option) => option.label}
                                    isOptionEqualToValue={(option, value) => option.value === value.value}
                                    onChange={handleNotificationDuplicateTypeChange}
                                    renderInput={(params) => <TextField {...params} variant="filled" color="primary" label="Type" />}
                                />
                            </Grid>
                            {(duplicateSettings.tour_override_mode === 0 || duplicateSettings.tour_override_mode === 1) && (
                                <Grid item xs={12} container>
                                    <Grid item container alignItems="center">
                                        <Radio value={0} name="non_override" checked={duplicateSettings.tour_override_mode === 0} onClick={() => setDuplicateSettings((prev) => ({ ...prev, tour_override_mode: 0 }))} />
                                        <Typography>Non overridable - Will be sent to all tours</Typography>
                                    </Grid>
                                    <Grid item container alignItems="center" justifyContent="flex-start">
                                        <Radio value={1} name="overridable" checked={duplicateSettings.tour_override_mode === 1} onClick={() => setDuplicateSettings((prev) => ({ ...prev, tour_override_mode: 1 }))} />
                                        <Typography>Overridable - Will be sent to all tours except</Typography>
                                        <Typography>the ones that have the same tour-specific notification</Typography>
                                    </Grid>
                                </Grid>
                            )}
                            {duplicateSettings.tour_override_mode === 2 && (
                                <>
                                    <Grid item xs={12} mt={2}>
                                        <Typography>Please choose the tour(s) for the variant:</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <RemoteAutocomplete filterSelected required InputProps={{ required: true }} resource="tour" label="Select Tour:" multiple variant="filled" size="small" onChange={handleNotificationDuplicateToursChange} value={duplicateSettings.tours} getOptionLabel={(opt) => opt.name} />
                                    </Grid>
                                </>
                            )}
                        </>
                    )}
                </ConfirmationDialog>
            ) : null} */}

            {/* <Grid>
                <Popper
                    open={new URLSearchParams(window?.location?.search).get('creatingNotification') === 'true'}
                    style={{ zIndex: 1, marginTop: '7rem' }}
                    anchorEl={addNotificationAnchorEl}
                    onClose={() => setAddNotificationAnchorEl(null)}
                    placement="bottom-start"
                    disablePortal={false}
                    modifiers={{
                        flip: {
                            enabled: true,
                        },
                        preventOverflow: {
                            enabled: true,
                            boundariesElement: 'scrollParent',
                        },
                    }}
                >
                    <AddNewNotification setLastUpdate={setLastUpdate} setSyncActive={setSyncActive} onClose={handleOnAddNotificationClose} syncActive={syncActive} isSmallScreen={isSmallScreen} />
                </Popper>
            </Grid> */}

            {isSmallScreen ? (
                <>
                    <Box className={classes.mobilePageHeader}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" px={1} pt={2} mb={1.5}>
                            <Typography fontSize={20} fontWeight={500} lineHeight="24px">
                                Strings
                            </Typography>
                        </Box>
                        <Box px={1} pb={1}>
                            <NotificationStringToolbar
                                resource={resource}
                                handleCreateNotification={() => history.push('/notifications/strings/create')}
                                setQuery={setQuery}
                                query={query}
                                selectedIds={selectedIds}
                                isSmallScreen={isSmallScreen}
                                roleMap={roleMap}
                                batchDelete={onBatchDelete}
                                syncActive={syncActive}
                            />
                        </Box>
                    </Box>
                    <Box className={classes.mobileListRegion}>
                        <Box className={classes.recordListWrapper}>{stringsRecordList}</Box>
                    </Box>
                </>
            ) : (
                <>
                    <Grid item xs={12} pt={2} pl={2} pr={2} container justifyContent="space-between" alignItems="center">
                        <Typography fontSize={20} fontWeight={500} fontHeight={24}>
                            Strings
                        </Typography>
                        <Grid item xs="auto" container direction="row-reverse" alignItems="center">
                            {hasPerm('notification', 'write') && (
                                <Box>
                                    <Button color="primary" variant="contained" onClick={(ev) => history.push(`/notifications/strings/create`)}>
                                        <AddIcon />
                                        &nbsp;New string
                                    </Button>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                    <Grid item xs={12} className={classes.stringsCard} style={{ margin: '8px 1rem 1rem', border: 'solid 1px #D8D8D8', borderRadius: '12px' }}>
                        <Grid item xs={12} p={2} pt={1} pb={0}>
                            <NotificationStringToolbar
                                resource={resource}
                                handleCreateNotification={() => history.push('/notifications/strings/create')}
                                setQuery={setQuery}
                                query={query}
                                selectedIds={selectedIds}
                                isSmallScreen={isSmallScreen}
                                roleMap={roleMap}
                                batchDelete={onBatchDelete}
                                syncActive={syncActive}
                            />
                        </Grid>
                        <Grid item xs={12} className={classes.recordListWrapper}>
                            {stringsRecordList}
                        </Grid>
                    </Grid>
                </>
            )}
        </div>
    );
};

export default NotificationStringList;